'use client'

import { useState } from 'react'
import { ChevronDown, ChevronUp, CircleCheck, CircleX } from 'lucide-react'

export interface Finding {
  text: string
  status: 'good' | 'bad' | 'neutral'
}

interface ScoreCardProps {
  title: string
  icon: React.ReactNode
  score: number
  maxScore: number
  findings: Finding[]
  details?: React.ReactNode
  color: string
}

export default function ScoreCard({
  title,
  icon,
  score,
  maxScore,
  findings,
  details,
  color,
}: ScoreCardProps) {
  const [expanded, setExpanded] = useState(false)
  const pct = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0

  return (
    <div className='bg-background border border-border/60 rounded-2xl p-5 flex flex-col gap-3'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <span style={{ color }}>{icon}</span>
          <h3 className='text-sm font-semibold text-foreground'>{title}</h3>
        </div>
        <span
          className='text-xs font-bold rounded-full px-2.5 py-0.5'
          style={{
            backgroundColor: `color-mix(in srgb, ${color} 8%, transparent)`,
            color,
          }}
        >
          {score}/{maxScore}
        </span>
      </div>

      {/* Score bar */}
      <div className='h-2 rounded-full bg-muted overflow-hidden'>
        <div
          className='h-full rounded-full transition-all duration-700 ease-out'
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>

      {/* Findings */}
      <ul className='space-y-1'>
        {findings.map((f, i) => (
          <li
            key={i}
            className='text-xs text-foreground/80 flex items-start gap-1.5'
          >
            {f.status === 'good' ? (
              <CircleCheck
                size={14}
                className='mt-0.5 shrink-0'
                style={{ color: '#22c55e' }}
              />
            ) : f.status === 'bad' ? (
              <CircleX
                size={14}
                className='mt-0.5 shrink-0'
                style={{ color: '#F90B8A' }}
              />
            ) : (
              <span
                className='mt-1 h-1.5 w-1.5 rounded-full shrink-0'
                style={{ backgroundColor: color }}
              />
            )}
            {f.text}
          </li>
        ))}
      </ul>

      {/* Expandable details */}
      {details && (
        <>
          <button
            onClick={() => setExpanded(!expanded)}
            className='flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors self-start'
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {expanded ? 'Hide details' : 'Show details'}
          </button>
          {expanded && (
            <div className='text-xs text-foreground/70 bg-muted rounded-lg p-3 overflow-auto max-h-48'>
              {details}
            </div>
          )}
        </>
      )}
    </div>
  )
}
